import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { BookOpen } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { authApi } from '@/api/auth'
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton'
import { GuestAuthShell } from '@/components/auth/GuestAuthShell'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { useMinCopy } from '@/i18n/useUiLocale'
import { useAuthStore } from '@/store/authStore'

type LoginFormValues = {
  email: string
  password: string
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const setAuth = useAuthStore((state) => state.setAuth)
  const t = useMinCopy({ preferGuest: true }).auth
  const successMessage = (location.state as { message?: string } | null)?.message

  const schema = useMemo(
    () =>
      z.object({
        email: z.string().email(t.invalidEmail),
        password: z.string().min(1, t.passwordRequired),
      }),
    [t.invalidEmail, t.passwordRequired],
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  useEffect(() => {
    if (successMessage) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, navigate, successMessage])

  useEffect(() => {
    if (searchParams.get('error') === 'google_auth_failed') {
      setError('root', {
        message: t.googleAuthFailed,
      })
      navigate('/login', { replace: true })
    }
  }, [navigate, searchParams, setError, t.googleAuthFailed])

  const onSubmit = async (data: LoginFormValues) => {
    clearErrors('root')

    try {
      const loginResponse = await authApi.login(data)
      const profileResponse = await authApi.getProfile(loginResponse.data.accessToken)
      setAuth(loginResponse.data.accessToken, profileResponse.data)
      navigate('/dashboard', { replace: true })
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        setError('root', { message: t.unableSignIn })
        return
      }

      if (error.response?.status === 401) {
        setError('email', { message: t.invalidCredentials })
        return
      }

      if (
        error.response?.status === 403 &&
        (error.response.data as { error?: string } | undefined)?.error === 'EMAIL_NOT_VERIFIED'
      ) {
        navigate('/verify-email', { state: { email: data.email } })
        return
      }

      if (error.response?.status === 429) {
        setError('root', { message: t.tooManyAttempts })
        return
      }

      setError('root', { message: t.unableSignIn })
    }
  }

  return (
    <GuestAuthShell>
      <Card className="relative w-full max-w-md border-white/60 bg-white/90 shadow-2xl backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <BookOpen className="h-6 w-6" />
          </div>
          <CardTitle>{t.loginTitle}</CardTitle>
          <CardDescription>{t.loginDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            {successMessage ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {successMessage}
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                hasError={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email ? (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="password">{t.password}</Label>
                <Link className="text-sm font-medium text-primary hover:underline" to="/reset-password">
                  {t.forgotPassword}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                hasError={Boolean(errors.password)}
                {...register('password')}
              />
              {errors.password ? (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              ) : null}
            </div>

            {errors.root?.message ? (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            ) : null}

            <Button type="submit" loading={isSubmitting}>
              {t.signIn}
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{t.or}</span>
              </div>
            </div>

            <GoogleSignInButton returnUrl="/dashboard" label={t.continueWithGoogle} />

            <p className="text-center text-sm text-muted-foreground">
              {t.noAccount}{' '}
              <Link className="font-medium text-primary hover:underline" to="/register">
                {t.registerLink}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </GuestAuthShell>
  )
}
